import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { coinApi } from '../../../api/coin';
import { createdCustomList } from '../../../store/reducers/segment';
import { counter } from '../../../tools/Counter';
import { shortenAddress } from '../../../utils/shortenAddress';
import { ethFormat } from '../../../utils/singleAssetPage/parseData';
import { supportedBlockchains } from '../../../utils/supportedBlockchains';
import { aliasData, aliasTooltipInfo } from '../../../utils/wallets';
import ChainList from '../../base/ChainLogo/ChainList';
import { showErrorMessage, showSuccessMessage } from '../../base/Notifications';
import Table from '../../base/Table';
import tableStyles from '../../base/Table/Table.module.scss';
import Export from '../Buttons/Export';
import Snapshot from '../Buttons/Snapshot';
import ItemPreview from '../ItemPreview';
import ConfirmModal from '../modals/ConfirmModal';
import DownloadModal from '../modals/DownloadModal';
import ListConfirmationModal from '../modals/ListConfirmationModal';
import SaveSnapshotModal from '../modals/SaveSnapshotModal';
import AgeDate from '../Table/AgeDate';
import Header from '../Table/Header';
import Tooltip from '../Tooltip';
import styles from './HoldersList.module.scss';

const MemoizedTable = React.memo(Table);

const HoldersList = ({
  data,
  isLoading,
  page,
  setPage,
  setDesc,
  limit,
  setLimit,
  desc,
  order,
  setOrder,
  result,
  downloadCSV,
  nft,
  audience,
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { name, network } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);
  const [tableData, setTableData] = useState([]);

  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotNft, snapshotNftResult] =
    coinApi.usePostSnapshotOfNftHolderListMutation();
  const [snapshotCoin, snapshotCoinResult] =
    coinApi.usePostSnapshotOfCoinHolderListMutation();
  const [header, setHeader] = useState([]);

  useEffect(() => {
    if (result.isLoading) {
      setIsOpen(true);
    }
    if (result.isSuccess && result.data) {
      const handleOpen = () => {
        setTimeout(() => {
          window.location.replace(result.data[0].link);
        }, 1000);
      };
      handleOpen();
    }
    if (result.error?.status === 403) {
      setIsOpen(false);
    }
    return () => clearTimeout();
  }, [result]);

  useEffect(() => {
    if (snapshotCoinResult.isSuccess) {
      showSuccessMessage('List was successfully saved');
      dispatch(createdCustomList(snapshotCoinResult?.data?.id));
      setTimeout(() => {
        dispatch(createdCustomList(null));
      }, 10000);
      setShowSnapshotModal(false);
      snapshotCoinResult.reset();
    }
    if (snapshotCoinResult.isError) {
      showErrorMessage(
        snapshotCoinResult?.error?.data?.detail || 'Something went wrong'
      );
      setShowSnapshotModal(false);
      snapshotCoinResult.reset();
    }
  }, [dispatch, snapshotCoinResult]);

  useEffect(() => {
    if (snapshotNftResult.isSuccess) {
      showSuccessMessage('List was successfully saved');
      dispatch(createdCustomList(snapshotNftResult?.data?.id));
      setTimeout(() => {
        dispatch(createdCustomList(null));
      }, 10000);
      setShowSnapshotModal(false);
      snapshotNftResult.reset();
    }
    if (snapshotNftResult.isError) {
      showErrorMessage(
        snapshotNftResult?.error?.data?.detail || 'Something went wrong'
      );
      setShowSnapshotModal(false);
      snapshotNftResult.reset();
    }
  }, [dispatch, snapshotNftResult]);

  useEffect(() => {
    const baseHeaders = [
      { field: 'address_name', title: 'Address Name' },
      { field: '', title: 'Alias' },
      { field: 'portfolio_value', title: 'Portfolio Value' },
      { field: 'age', title: 'Wallet Age' },
      {
        field: '',
        title: <div className="d-flex justify-content-center">Active On</div>,
      },
    ];
    const sinceHeader = { field: 'since', title: 'Holding Since' };
    const balanceHeader = { field: 'token_qty', title: 'Balance' };
    const shareHeader = {
      field: 'share_of_portfolio',
      title: 'Share of Portfolio',
    };
    const headersWithSince = [...baseHeaders];

    if (!['lost'].includes(audience)) {
      headersWithSince.splice(2, 0, balanceHeader);
      headersWithSince.splice(3, 0, shareHeader);
    }

    if (['current', 'relevant'].includes(audience)) {
      headersWithSince.splice(5, 0, sinceHeader);
    } else if (tableData.includes('since')) {
      const temp = [...tableData];
      temp.forEach((elem) => {
        delete elem.since;
      });
      setTableData(temp);
    }

    setHeader(headersWithSince);
  }, [audience]);

  useEffect(() => {
    if (data?.result) {
      const temp = data.result.map((elem, index) => ({
        address_name: (
          <>
            <span
              className={`d-block ${tableStyles.underline_on_hover}`}
              data-for={`Name_${elem.ALID || elem.address_name || elem.address}`}
              data-tip
            >
              <Link
                role="presentation"
                className={`${styles.name} text-truncate`}
                onClick={() => {
                  setSearchParams({
                    ...Object.fromEntries([...searchParams]),
                    scroll: window.scrollY,
                  });
                }}
                to={`/profile/${elem.ALID || elem.address}`}
              >
                {shortenAddress(elem.address_name || elem.address)}
              </Link>
            </span>
            <ItemPreview
              id={`Name_${elem.ALID || elem.address_name || elem.address}`}
              data={{
                id: elem.address,
                name: elem.address,
                alid: elem.ALID || '',
                type: 'wallet',
              }}
            />
          </>
        ),
        alias: elem.alias ? (
          <span className="d-block">
            <div className="d-flex gap-2 align-items-baseline">
              <span data-for={`alias ${index}`} data-tip>
                {aliasData[elem.alias_type]}
              </span>
              <Tooltip
                info={aliasTooltipInfo[elem.alias_type]}
                id={`alias ${index}`}
                place="top"
              />
              <span
                data-for={`fullAlias ${index}`}
                data-tip={elem.alias.length > 10 ? elem.alias : ''}
              >
                {elem.alias.length > 10
                  ? `${elem.alias.substring(0, 10)}...`
                  : elem.alias}
              </span>
              {elem.alias.length > 2 && (
                <Tooltip
                  info={elem.alias}
                  id={`fullAlias ${index}`}
                  place="top"
                />
              )}
            </div>
          </span>
        ) : (
          '-'
        ),
        token_qty: `${
          elem.token_qty && elem.token_qty >= 1
            ? nft
              ? ethFormat(elem.token_qty, 1)
              : ethFormat(elem.token_qty)
            : '<1'
        } (${elem.value !== null ? `$${ethFormat(elem.value)}` : '?'})`,
        share_of_portfolio:
          elem.share_of_portfolio && elem.share_of_portfolio >= 1
            ? `${elem.share_of_portfolio}%`
            : '<1%',
        portfolio_value: `$${ethFormat(elem.portfolio_value)}`,
        since: <AgeDate date={elem.since} defaultText="No Transactions" />,
        age: <AgeDate date={elem.age} defaultText="No Transactions" isAge />,
        network: (
          <div className={styles.chains}>
            <ChainList chains={supportedBlockchains(elem.active_on)} small />
          </div>
        ),
      }));
      if (!['current', 'relevant'].includes(audience)) {
        temp.forEach((elem) => {
          delete elem.since;
        });
      }
      if (['lost'].includes(audience)) {
        temp.forEach((elem) => {
          delete elem.token_qty;
          delete elem.share_of_portfolio;
        });
      }
      setTableData(temp);
    } else {
      setTableData([]);
    }
  }, [data, audience]);

  const handleSnapshotClick = () => {
    const total = data?.count || 0;
    if (total >= 50000) {
      setShowConfirmationModal(true);
    } else {
      setShowSnapshotModal(true);
    }
  };

  return (
    <div className={`${styles.wrapper} d-flex w-100 flex-column`}>
      <Header
        title="Profile List"
        tooltip="This list may include multiple profiles associated with the same wallet address"
        info={counter(data?.count, page, limit)}
        actions={
          <>
            <Export
              onClick={() =>
                downloadCSV({
                  id: name,
                  wait: true,
                  blockchain: network || 'ethereum',
                  audience,
                })
              }
              tooltip="Export Profile List to CSV"
              tooltipWarnings={[
                'No more than 50,000 profiles will be downloaded',
              ]}
            />
            <Snapshot
              tooltip="Save snapshot as List"
              onClick={handleSnapshotClick}
              number={data?.count || 0}
            />
          </>
        }
      />

      <div className="w-100">
        <MemoizedTable
          total={data?.count}
          data={tableData}
          desc={desc}
          setDesc={setDesc}
          ordering={order}
          setOrdering={setOrder}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
          headers={header}
          isLoading={isLoading}
          min={25}
        />
      </div>
      {isOpen && result?.error?.status !== 403 ? (
        <DownloadModal result={result} setIsOpen={setIsOpen} />
      ) : null}
      {result?.error?.status === 403 ? (
        <ConfirmModal
          title="We are sorry..."
          description="The feature you’re trying to use has restricted access.
                 Please reach out to your Customer Success Manager."
          buttonName="Okay"
          onSubmit={() => result.reset()}
        />
      ) : null}
      {showSnapshotModal && (
        <SaveSnapshotModal
          value={snapshotName}
          setValue={setSnapshotName}
          onSubmit={() => {
            try {
              if (pathname.split('/')[1] === 'nfts') {
                snapshotNft({
                  id: name,
                  name: snapshotName,
                  network,
                  audience,
                });
              } else {
                snapshotCoin({ id: name, name: snapshotName, audience });
              }
            } catch (err) {
              showErrorMessage(err);
            }
          }}
          onCancel={() => setShowSnapshotModal(false)}
          loading={
            snapshotCoinResult.isLoading ||
            snapshotCoinResult.isFetching ||
            snapshotNftResult.isLoading ||
            snapshotNftResult.isFetching
          }
        />
      )}
      {showConfirmationModal && (
        <ListConfirmationModal
          number={data?.count}
          onSubmit={() => {
            setShowConfirmationModal(false);
            setShowSnapshotModal(true);
          }}
          onCancel={() => setShowConfirmationModal(false)}
        />
      )}
    </div>
  );
};

export default HoldersList;
